export type TableValueFormat = "currency" | "percent" | "ratio" | "number"

/**
 * Format a raw numeric value for exact display in a data table — up to 2 decimal
 * places with trailing zeros trimmed (`1.10` → "1.1", `1.00` → "1"), abbreviated
 * with K/M suffixes at 1,000+ (currency/number only).
 *
 * Distinct from `@/features/dashboard/lib/format`'s `formatMetricValue` (used for
 * summary cards): that one scales K/M with a fixed 1 decimal and expects percent
 * inputs already pre-multiplied by 100. This formatter does the percent ×100
 * scaling itself, since the My Brand row's ctr/cr/thumbstop/holdrate fields are raw
 * 0-1 fractions.
 *
 * `null`/`undefined`/`NaN` (e.g. a SAFE_DIVIDE zero-denominator result) render as
 * "—", never "0" or "$0", so an undefined metric isn't confused with a real zero.
 *
 * A value that rounds to zero at 2-decimal precision renders bare "0" (via the
 * trailing-zero trim below) — this covers a literal `0` as well as an unrounded
 * aggregate (e.g. a BigQuery AVG with no ROUND()) landing on something like
 * `0.0004`, which would otherwise still show "0.00" and read as false precision.
 */
export function formatTableValue(
  value: number | null | undefined,
  format: TableValueFormat,
  currencySymbol = "$",
  decimals = 2,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—"

  if (format === "ratio") {
    return `${trimTrailingZeros(value, decimals)}x`
  }

  if (format === "percent") {
    return `${trimTrailingZeros(value * 100, decimals)}%`
  }

  const prefix = format === "currency" ? currencySymbol : ""
  const abs = Math.abs(value)

  if (abs >= 1_000_000) {
    return `${prefix}${trimTrailingZeros(value / 1_000_000, decimals)}M`
  }
  if (abs >= 1_000) {
    return `${prefix}${trimTrailingZeros(value / 1_000, decimals)}K`
  }
  return `${prefix}${trimTrailingZeros(value, decimals)}`
}

/**
 * Round to `decimals` and strip trailing zeros / an unneeded decimal point —
 * `1.00` → "1", `1.10` → "1.1", `1.23` → "1.23". Also normalizes `-0.00` to "0".
 */
function trimTrailingZeros(value: number, decimals = 2): string {
  return Number(value.toFixed(decimals)).toString()
}
